import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardBarChart from './charts/DashboardBarChart';
import {
    CAMPAIGN_CHANNEL_STATS,
    CAMPAIGN_INFORMATION_STATS,
    EMAIL_STATS_WINDOWS,
} from '../dashboardStatsConfig';
import { parseStatValue } from '../dashboardStatUtils';

const formatCount = (count) =>
    new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
    }).format(count);

const formatShare = (count, total) => {
    if (total <= 0) {
        return '0%';
    }
    return `${Math.round((count * 100) / total)}%`;
};

function DashboardCampaignStats({ getStat, isLoading, loadError, lastUpdated }) {
    const [days, setDays] = useState(30);

    const campaignRows = useMemo(() => {
        const rows = CAMPAIGN_INFORMATION_STATS.map((stat) => ({
            ...stat,
            count: parseStatValue(getStat(`${stat.keyPrefix}_${days}_DAYS`)),
        }));
        const total = rows.reduce((sum, row) => sum + row.count, 0);
        return rows.map((row) => ({
            ...row,
            countLabel: formatCount(row.count),
            shareLabel: formatShare(row.count, total),
        }));
    }, [days, getStat]);

    const totalSent = useMemo(
        () => campaignRows.reduce((sum, row) => sum + row.count, 0),
        [campaignRows]
    );

    const channelSeries = useMemo(
        () =>
            CAMPAIGN_CHANNEL_STATS.map((channel) => ({
                label: channel.label,
                color: channel.color,
                value: channel.keyPrefixes.reduce(
                    (sum, keyPrefix) => sum + parseStatValue(getStat(`${keyPrefix}_${days}_DAYS`)),
                    0
                ),
            })),
        [days, getStat]
    );

    const channelTotal = channelSeries.reduce((sum, item) => sum + item.value, 0);
    const hasChannelData = channelTotal > 0;

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--violet">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-megaphone" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">Campaign Information</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `Blast and newsletter sends · Last ${days} days · Updated ${lastUpdated}`
                                    : 'Blast and newsletter messages sent by email and SMS'}
                            </p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="dashboard-email-stats__toggle" role="group" aria-label="Campaign stats period">
                            {EMAIL_STATS_WINDOWS.map((window) => (
                                <button
                                    key={window.days}
                                    type="button"
                                    className={`dashboard-email-stats__toggle-btn${days === window.days ? ' is-active' : ''}`}
                                    onClick={() => setDays(window.days)}
                                >
                                    {window.label}
                                </button>
                            ))}
                        </div>
                        <Link to="/blast" className="btn btn-sm btn-light-primary rounded-pill">
                            Open blast
                            <i className="bi bi-arrow-right ms-1" />
                        </Link>
                        <Link to="/newsletter" className="btn btn-sm btn-light-primary rounded-pill">
                            Open newsletter
                            <i className="bi bi-arrow-right ms-1" />
                        </Link>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="row g-3">
                        <div className="col-12 col-xl-4">
                            <div className="dashboard-stat-tile dashboard-stat-tile--skeleton" style={{ minHeight: 280 }} />
                        </div>
                        <div className="col-12 col-xl-8">
                            <div className="dashboard-roi" aria-hidden="true">
                                <div className="dashboard-roi__summary dashboard-stat-tile--skeleton" style={{ minHeight: '4.5rem' }} />
                                <div className="dashboard-roi__table">
                                    {Array.from({ length: 4 }).map((_, index) => (
                                        <div
                                            key={`campaign-row-skel-${index}`}
                                            className="dashboard-roi__row dashboard-stat-tile--skeleton"
                                            style={{ minHeight: '3rem', border: 0 }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row g-3 align-items-stretch">
                        <div className="col-12 col-xl-4">
                            <div className="dashboard-patient-chart dashboard-campaign-channel-chart h-100">
                                <div className="dashboard-patient-chart__header">
                                    <span className="dashboard-patient-chart__icon" aria-hidden="true">
                                        <i className="bi bi-broadcast-pin" />
                                    </span>
                                    <div>
                                        <h3 className="dashboard-patient-chart__title mb-0">By Channel</h3>
                                        <p className="dashboard-patient-chart__subtitle mb-0">
                                            Total: <strong>{formatCount(channelTotal)}</strong>
                                        </p>
                                    </div>
                                </div>
                                {hasChannelData ? (
                                    <DashboardBarChart series={channelSeries} title="By Channel" />
                                ) : (
                                    <div className="dashboard-empty-state">
                                        <i className="bi bi-bar-chart me-2" />
                                        No data available
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-12 col-xl-8">
                            <div className="dashboard-roi">
                                <div className="dashboard-roi__summary">
                                    <div className="dashboard-roi__summary-copy">
                                        <span className="dashboard-roi__summary-label">Total messages sent</span>
                                        <span className="dashboard-roi__summary-hint">
                                            Email and SMS blast / newsletter deliveries in the selected window
                                        </span>
                                    </div>
                                    <span className="dashboard-roi__summary-value">{formatCount(totalSent)}</span>
                                </div>

                                <div className="dashboard-roi__table" role="table" aria-label="Campaign information breakdown">
                                    <div className="dashboard-roi__head" role="row">
                                        <span role="columnheader">Metric</span>
                                        <span role="columnheader">Share</span>
                                        <span role="columnheader">Messages</span>
                                    </div>

                                    {campaignRows.map((stat) => (
                                        <div key={stat.keyPrefix} className="dashboard-roi__row" role="row">
                                            <div className="dashboard-roi__metric" role="cell">
                                                <span className="dashboard-roi__metric-icon" aria-hidden="true">
                                                    <i className={`bi ${stat.icon}`} />
                                                </span>
                                                <span className="dashboard-roi__metric-label">{stat.label}</span>
                                            </div>
                                            <span className="dashboard-roi__calc" role="cell">
                                                {stat.shareLabel}
                                            </span>
                                            <span className="dashboard-roi__amount" role="cell">
                                                {stat.countLabel}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default DashboardCampaignStats;
